"use client"

import { Skeleton } from "@chakra-ui/react"
import axios from "axios"
import React, { useEffect, useRef, useState } from "react"
import { NotificationManager } from "react-notifications"
import { useSelector } from "react-redux"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import DownArrow from "../../assets/svgs/down-arrow.svg"
import sortAsce from "../../assets/svgs/sort-ascending.svg"
import sortDesc from "../../assets/svgs/sort-descending.svg"
import swap from "../../assets/svgs/swap-vertical.svg"
import UpArrow from "../../assets/svgs/up-arrow.svg"
import ActionsMenu from "../../components/ActionsMenu"
import ConfirmationModal from "../../components/ConfirmationModals/ConfirmationModal"
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal"
import Loader from "../../components/Loader"
import ContactPagination from "../../components/Pagination/ContactPagination"
import DataAsOfNotice from "../../components/DataAsOfNotice"
import useAuth from "../../hooks/useAuth"
import BaseLayout from "../../layouts/BaseLayout"
import { ROUTES } from "../../routes/routeKeys"
import { BASE_URL } from "../../utils/Element"
import { initialContractCloseFilterData } from "../../utils/initialData"
import ContractCloseAdvanceFilter from "./components/ContractCloseAdvanceFilter"
import ExpandedRowContent from "./components/ExpandedRowContent"
import MarketMetrics from "./components/MarketMetrics"
import PropertyPortfolioModal from "./components/PropertyPortfolioModal"
// import TransactionDashboard from "./components/transactionDashboard"
import TransactionDashboard from "./components/transactionDashboardNew"

const ContractToClosed = () => {
  const [config] = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // ─── States ─────────────────────────────────────────────────────
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceTimer = useRef(null)
  const [listData, setListData] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState("")
  const [selectedPortfolioId, setSelectedPortfolioId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemPerPage, setItemPerPage] = useState(10)
  const [sortBy, setSortBy] = useState("id")
  const [sortDirection, setSortDirection] = useState("asc")
  const [disableDelete, setDisableDelete] = useState(false)
  const [showPropertyModal, setShowPropertyModal] = useState(false)
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed)
  const [expandedRows, setExpandedRows] = useState({})

  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  })

  const [filterData, setFilterData] = useState(initialContractCloseFilterData)
  const [advanceFilterData, setAdvanceFilterData] = useState([])
  const [appliedFilterData, setAppliedFilterData] = useState(initialContractCloseFilterData)
  const [viewAs, setViewAs] = useState('company')

  // Tab from URL with fallback
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = parseInt(searchParams.get("tab"))
    return [0, 1, 2, 3].includes(tabFromUrl) ? tabFromUrl : 2
  })

  // Update URL
  useEffect(() => {
    setSearchParams({ tab: activeTab })
  }, [activeTab, setSearchParams])

  // ==================== STRONG RESET ON TAB CHANGE ====================
  useEffect(() => {
    const resetAllFilters = () => {
      setAdvanceFilterData([])
      setFilterData({ ...initialContractCloseFilterData })
      setAppliedFilterData({ ...initialContractCloseFilterData })
      setSearch("")
      setDebouncedSearch("")
      setCurrentPage(1)
      setSortBy("id")
      setSortDirection("asc")
    }

    resetAllFilters()

    const timer = setTimeout(() => {
      fetchData({ ...initialContractCloseFilterData }, [])
    }, 50)

    return () => clearTimeout(timer)
  }, [activeTab])

  // ─── Debounce Search ───────────────────────────────────────────
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    debounceTimer.current = setTimeout(() => {
      if (search.length === 0 || search.length >= 3) {
        setDebouncedSearch(search.trim())
      }
    }, 450)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [search])

  // ─── Query Params ───────────────────────────────────────────────
  const buildQueryParams = (filters) => {
    let params = `page=${currentPage}&per_page=${itemPerPage}`

    if (debouncedSearch) params += `&search=${encodeURIComponent(debouncedSearch)}`

    if (filters.current_phase) params += `&current_phase=${encodeURIComponent(filters.current_phase)}`
    if (filters.next_action) params += `&next_action=${encodeURIComponent(filters.next_action)}`
    if (filters.advisor) params += `&advisor=${encodeURIComponent(filters.advisor)}`
    if (filters.status) params += `&status=${encodeURIComponent(filters.status)}`

    if (filters.property?.id) params += `&property=${encodeURIComponent(filters.property.id)}`
    if (filters.contact?.id) params += `&contact=${encodeURIComponent(filters.contact.id)}`
    if (filters.acquisition_contact?.id) params += `&acquisition_contact=${encodeURIComponent(filters.acquisition_contact.id)}`
    if (filters.disposition_contact?.id) params += `&disposition_contact=${encodeURIComponent(filters.disposition_contact.id)}`

    if (filters.min_contract_price) params += `&min_contract_price=${encodeURIComponent(filters.min_contract_price)}`
    if (filters.max_contract_price) params += `&max_contract_price=${encodeURIComponent(filters.max_contract_price)}`
    if (filters.min_contract_cap_rate) params += `&min_contract_cap_rate=${encodeURIComponent(filters.min_contract_cap_rate)}`
    if (filters.max_contract_cap_rate) params += `&max_contract_cap_rate=${encodeURIComponent(filters.max_contract_cap_rate)}`
    if (filters.noi) params += `&noi=${encodeURIComponent(filters.noi)}`

    if (sortBy && sortBy !== "id") {
      params += `&sort_by=${sortBy}&sort_direction=${sortDirection}`
    }

    return params
  }

  const fetchData = (filters = {}, advanceFilters = []) => {
    const queryParams = buildQueryParams(filters)
    const validAdvanceFilters = advanceFilters.filter(group =>
      group?.filters?.some(f => f.field && f.operator)
    )
    const advanceFiltersQuery = encodeURIComponent(JSON.stringify(validAdvanceFilters))

    setLoading(true)

    axios
      .get(`${BASE_URL}/contract-to-close-listing?${queryParams}&advance_filters=${advanceFiltersQuery}&active_tab=${activeTab}`, config)
      .then((res) => {
        const value = res?.data?.data?.contracts_to_close || {}
        setListData(value.data || [])
        setPaginationData({
          totalItems: value.total ?? 0,
          from: value.from ?? 0,
          to: value.to ?? 0,
          totalPages: value.last_page ?? 0,
        })
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchData(filterData, advanceFilterData)
  }, [debouncedSearch, currentPage, itemPerPage, activeTab, sortBy, sortDirection])

  const onSuccess = (filters, advanceFilters) => {
    const finalFilters = filters || filterData
    const advFilters = advanceFilters !== undefined ? advanceFilters : advanceFilterData
    setAppliedFilterData(finalFilters)
    fetchData(finalFilters, advFilters)
  }

  const handleNext = () => {
    if (currentPage < paginationData.totalPages) setCurrentPage(currentPage + 1)
  }

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleDelete = () => {
    setDisableDelete(true)
    axios.delete(`${BASE_URL}/contract-to-close/${selectedId}`, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowDeleteModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
      .finally(() => setDisableDelete(false))
  }

  const handleConfirm = () => {
    axios.put(`${BASE_URL}/mark-closed/${selectedId}`, { closed: true }, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowConfirmModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return "text-red-500"
      case 2: return "text-gray-500"
      case 3: return "text-gray-500"
      default: return "text-red-500"
    }
  }


  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">Contract to Close</p>
      </div>

      <div className="md:flex justify-between items-center mb-4 mt-2 px-1">
        <div className="flex gap-4 items-center flex-wrap">
          {(activeTab === 0 || activeTab === 2 || activeTab === 3) && (
            <ContractCloseAdvanceFilter
              key={`advance-filter-${activeTab}`}   // Very important for reset
              filterData={advanceFilterData}
              onSetFilterData={setAdvanceFilterData}
              activeTab={activeTab}
              from="Contract-To-Close"
              onCallApiAgain={(newAdvanceFilters) => {
                setAdvanceFilterData(newAdvanceFilters)
                fetchData(filterData, newAdvanceFilters)
              }}
            />
          )}

          {(activeTab === 0 || activeTab === 1) && (
            <div className="search-box contacts relative w-[250px]">
              <input
                type="text"
                className="body-S w-full"
                placeholder="Search name, address, city, state..."
                value={search}
                onChange={(e) => setSearch(e.target.value.startsWith(" ") ? e.target.value.trimStart() : e.target.value)}
                style={{ border: "1px solid #D8D8D8", marginTop: "0px", width: "100%" }}
              />
            </div>
          )}
        </div>

        <div className="flex gap-2 items-center flex-wrap">
          {activeTab === 2 && (
            <div className="flex flex-col mx-2">
              <div className="flex rounded-md border border-gray-200 overflow-hidden shadow-sm h-[38px]">
                <button 
                  className={`px-4 py-1.5 text-xs font-semibold ${viewAs === 'company' ? 'bg-[#0b8043] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setViewAs('company')}
                >
                  Company
                </button>
                <button 
                  className={`px-4 py-1.5 text-xs font-semibold border-l border-gray-200 ${viewAs === 'production' ? 'bg-[#0b8043] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setViewAs('production')}
                >
                  My Production
                </button>
              </div>
            </div>
          )}

          <div className="relative min-w-[240px]">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(parseInt(e.target.value))}
              className="w-full bg-white rounded-lg px-4 py-2.5 body-S dark-M cursor-pointer appearance-none"
            >
              <option value={0}>Transaction Details</option>
              <option value={1}>Critical Dates</option>
              <option value={2}>Transaction Dashboard</option>
              <option value={3}>Market Metrics</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
              <img src={DownArrow} alt="down" className="w-4 h-4" />
            </div>
          </div>
        </div>
      </div>

      {activeTab === 0 && (
        <div className="light-bg-L pb-3 table-container bg-white rounded-xl border border-gray-200 overflow-hidden relative min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
              <Loader />
            </div>
          )}
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {listData?.length === 0 && !loading ? (
              <p className="body-N text-center mt-6 p-10">No Data Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th><p>Details</p></th>
                    <th className="green-H">
                      <div className="table-header">
                        Property
                        <img role="button" src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} onClick={() => { setSortBy("property_name"); setSortDirection(sortDirection === "desc" ? "asc" : "desc") }} alt="sort" />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Key Contacts
                        <img role="button" src={sortBy !== "key_contacts" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} onClick={() => { setSortBy("key_contacts"); setSortDirection(sortDirection === "desc" ? "asc" : "desc") }} alt="sort" />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Next Milestone
                        <img role="button" src={sortBy !== "next_Milestone" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} onClick={() => { setSortBy("purchase_agreement_draft_distributed"); setSortDirection(sortDirection === "desc" ? "asc" : "desc") }} alt="sort" />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        Closing Progress
                        <img role="button" src={sortBy !== "executed_purchase_agreeement" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} onClick={() => { setSortBy("executed_purchase_agreeement"); setSortDirection(sortDirection === "desc" ? "asc" : "desc") }} alt="sort" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(Number(itemPerPage) || 10)].map((_, idx) => (
                      <tr key={idx} className="dark-M body-N">
                        <td><Skeleton height="20px" width="20px" /></td>
                        <td><Skeleton height="20px" width="180px" /></td>
                        <td><Skeleton height="20px" width="140px" /></td>
                        <td><Skeleton height="20px" width="140px" /></td>
                        <td><Skeleton height="20px" width="150px" /></td>
                      </tr>
                    ))
                  ) : listData?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="body-N text-center mt-6 p-10 border-0">
                        No Data Available
                      </td>
                    </tr>
                  ) : (
                    listData.map((el) => (
                    <React.Fragment key={el.id}>
                      <tr
                        className="dark-M body-N cursor-pointer"
                        onClick={(e) => {
                          if (e.target.closest(".actions-menu") || e.target.closest(".arrow-icon")) return
                          navigate(`${ROUTES.STAGE_DETAILS.replace(":id", el.id)}?tab=0`)
                        }}
                      >
                        <td>
                          <img role="button" src={expandedRows[el.id] ? UpArrow : DownArrow} alt="expand" className="sidebar-icons arrow-icon" onClick={(e) => { e.stopPropagation(); toggleRowDetails(el.id) }} />
                        </td>
                        <td className="head-5 capitalize flex items-start gap-3">
                          {el?.portfolio === 1 && (
                            <p onClick={(e) => { e.stopPropagation(); setSelectedPortfolioId(el?.link?.id); setShowPropertyModal(true) }}>+</p>
                          )}
                          <div className="flex-1">
                            <Link to={el?.portfolio === 0 ? ROUTES.PROPERTY_DETAILS.replace(":id", el?.property_id) : el?.link?.owner_info_type === "contact" ? ROUTES.CONTACT_DETAILS.replace(":id", el?.link?.owner?.id) : ROUTES.COMPANY_DETAILS.replace(":id", el?.link?.owner?.id)} onClick={(e) => { e.preventDefault(); navigate(el?.portfolio === 0 ? ROUTES.PROPERTY_DETAILS.replace(":id", el?.property_id) : el?.link?.owner_info_type === "contact" ? ROUTES.CONTACT_DETAILS.replace(":id", el?.link?.owner?.id) : ROUTES.COMPANY_DETAILS.replace(":id", el?.link?.owner?.id)) }}>
                              {el?.link?.property_name}<br />
                              {el?.link?.street_address}<br />
                              {el?.link?.state && el?.link?.city ? ` ${el?.link?.city}, ${el?.link?.state} ` : ""}
                            </Link>
                          </div>
                        </td>
                        <td>
                          <div>
                            <p>{el?.key_contact?.agent?.name}</p>
                            {el?.key_contact?.broker_details?.map((bd, i) => <p key={i}>{bd?.first_name} {bd?.last_name}</p>)}
                          </div>
                        </td>
                        <td>{el?.current_and_next_key?.next_key}</td>
                        <td>
                          <div className="w-full max-w-[150px] bg-gray-200 rounded-full h-2.5">
                            <div className="h-2.5 rounded-full bg-[#d1dbcf]" style={{ width: `${el?.contract_progress?.percentage || 0}%` }} />
                          </div>
                          <p className="text-xs mt-1 text-center">({el?.contract_progress?.percentage || 0}% complete)</p>
                        </td>
                      </tr>
                      {expandedRows[el.id] && (
                        <tr><td colSpan={5}><ExpandedRowContent data={el} /></td></tr>
                      )}
                    </React.Fragment>
                  ))
                )}
                </tbody>
              </table>
            )}
          </div>
          <ContactPagination paginationData={paginationData} activeTab={activeTab} handleNext={handleNext} handlePrev={handlePrev} onSuccess={onSuccess} itemPerPage={itemPerPage} handleItemPerPageSelect={(value) => { setItemPerPage(value); setCurrentPage(1) }} />
          <div className="px-6 pb-2">
            <DataAsOfNotice  />
          </div>
        </div>
      )}

      {/* ===================== LIST VIEW (Tab 1) ===================== */}
      {activeTab === 1 && (
        <div className="light-bg-L pb-3 table-container bg-white rounded-xl border border-gray-200 overflow-hidden relative min-h-[400px]">
          {loading && (
            <div className="absolute inset-0 z-10 flex justify-center items-center bg-white/40 backdrop-blur-[1px]">
              <Loader />
            </div>
          )}
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {listData?.length === 0 && !loading ? (
              <p className="body-N text-center mt-6 p-10">No Data Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th className="green-H">Property</th>
                    <th>DISPOSITION</th>
                    <th>ACQUISITION</th>
                    <th>EXECUTED LOI DATE</th>
                    <th>PSA DISTRIBUTED</th>
                    <th>PSA EXECUTED</th>
                    <th>EARNEST DEPOSIT DUE</th>
                    <th>DUE DILIGENCE DELIVERY</th>
                    <th>Title</th>
                    <th>Coordinate Inspection</th>
                    <th>Due Diligence Expiration</th>
                    <th>Estoppel & SNDA Ordered</th>
                    <th>Financing Approval</th>
                    <th>Estoppel & SNDA Received</th>
                    <th>Commission Invoice & Wiring Instructions</th>
                    <th>Closing Scheduled</th>
                    <th>More</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(Number(itemPerPage) || 10)].map((_, idx) => (
                      <tr key={idx} className="dark-M body-N">
                        <td><Skeleton height="20px" width="180px" /></td>
                        <td><Skeleton height="20px" width="100px" /></td>
                        <td><Skeleton height="20px" width="100px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="120px" /></td>
                        <td><Skeleton height="20px" width="40px" /></td>
                      </tr>
                    ))
                  ) : listData?.length === 0 ? (
                    <tr>
                      <td colSpan={17} className="body-N text-center mt-6 p-10 border-0">
                        No Data Available
                      </td>
                    </tr>
                  ) : (
                    listData.map((el) => (
                    <tr
                      key={el.id}
                      className="dark-M body-N cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`${ROUTES.STAGE_DETAILS.replace(":id", el.id)}?tab=1`)}
                    >
                      <td className="head-5 capitalize">
                        <Link to={el?.portfolio === 0 ? ROUTES.PROPERTY_DETAILS.replace(":id", el?.property_id) : el?.link?.owner_info_type === "contact" ? ROUTES.CONTACT_DETAILS.replace(":id", el?.link?.owner?.id) : ROUTES.COMPANY_DETAILS.replace(":id", el?.link?.owner?.id)} onClick={(e) => { e.preventDefault(); navigate(el?.portfolio === 0 ? ROUTES.PROPERTY_DETAILS.replace(":id", el?.property_id) : el?.link?.owner_info_type === "contact" ? ROUTES.CONTACT_DETAILS.replace(":id", el?.link?.owner?.id) : ROUTES.COMPANY_DETAILS.replace(":id", el?.link?.owner?.id)) }}>
                          {el?.link?.property_name}<br />
                          {el?.link?.street_address}<br />
                          {el?.link?.state && el?.link?.city ? ` ${el?.link?.city}, ${el?.link?.state} ` : ""}
                        </Link>
                      </td>
                      <td>{el?.executed?.offer?.disposition_name}</td>
                      <td>{el?.executed?.offer?.acquistion_name}</td>
                      <td>{el?.executed_loi_date}</td>
                      <td>{el?.purchase_agreement_draft_distributed_display}</td>
                      <td>{el?.executed_purchase_agreeement_display}</td>
                      <td className={getStatusColor(el?.earnest_deposite_confirmed_status)}>{el?.earnest_deposite_confirmed_display}</td>
                      <td className={getStatusColor(el?.due_diligence_delivery_receipt_confirmed_status)}>{el?.due_diligence_delivery_receipt_confirmed_display}</td>
                      <td className={getStatusColor(el?.title_status)}>{el?.title_display}</td>
                      <td className={getStatusColor(el?.cordinate_inspection_status)}>{el?.cordinate_inspection_display}</td>
                      <td className={getStatusColor(el?.due_diligence_expiration_date_status)}>{el?.due_diligence_expiration_date_display}</td>
                      <td className={getStatusColor(el?.estoppel_snda_ordered_status)}>{el?.estoppel_snda_ordered_display}</td>
                      <td className={getStatusColor(el?.financing_approval_status)}>{el?.financing_approval_display}</td>
                      <td className={getStatusColor(el?.estoppel_snda_received_status)}>{el?.estoppel_snda_received_display}</td>
                      <td className={getStatusColor(el?.comission_invoice_wiring_instruction_status)}>{el?.comission_invoice_wiring_instruction_display}</td>
                      <td className={getStatusColor(el?.scheduled_closing_status)}>{el?.scheduled_closing_display}</td>
                      <td className="flex gap-3 items-center justify-center actions-menu" onClick={(e) => e.stopPropagation()}>
                        <ActionsMenu
                          handleEdit={() => navigate(ROUTES.EDIT_CONTACT_CLOSE.replace(":id", el?.id))}
                          handleDelete={() => { setSelectedId(el?.id); setShowDeleteModal(true) }}
                          showOtherOption={true}
                          otherOptionTitle="Close Contract"
                          handleOtherOption={() => { setShowConfirmModal(true); setSelectedId(el?.id) }}
                        />
                      </td>
                    </tr>
                  ))
                )}
                </tbody>
              </table>
            )}
          </div>
          <ContactPagination paginationData={paginationData} activeTab={activeTab} handleNext={handleNext} handlePrev={handlePrev} onSuccess={onSuccess} itemPerPage={itemPerPage} handleItemPerPageSelect={(value) => { setItemPerPage(value); setCurrentPage(1) }} />
          <div className="px-6 pb-2">
            <DataAsOfNotice />
          </div>
        </div>
      )}

      {/* Transaction Dashboard */}
      {activeTab === 2 && (
        <TransactionDashboard
          loading={loading}
          isSidebarCollapsed={isSidebarCollapsed}
          from="contract-to-close"
          onCallApi={onSuccess}
          advanceFilterData={advanceFilterData}
          activeTab={activeTab}
          viewAs={viewAs}
        />
      )}

      {/* Market Metrics */}
      {activeTab === 3 && (
        <MarketMetrics
          loading={loading}
          isSidebarCollapsed={isSidebarCollapsed}
          from="contract-to-close"
          onCallApi={onSuccess}
          advanceFilterData={advanceFilterData}
          activeTab={activeTab}
        />
      )}

      {/* Modals */}
      <ConfirmationModal showModal={showConfirmModal} onClose={() => setShowConfirmModal(false)} handleConfirm={handleConfirm} />
      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />

      <PropertyPortfolioModal
        isOpen={showPropertyModal}
        onClose={() => { setShowPropertyModal(false); setSelectedProperty(null) }}
        id={selectedPortfolioId}
        property={selectedProperty}
      />
    </BaseLayout>
  )
}

export default ContractToClosed